import { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import { i18n } from 'app/utils/i18n';
import { useClonePreview, useCloneDocument } from 'app/api/documents/document-queries';
import CloneDocumentModal from 'app/views/documents/CloneDocumentModal';
import Spinner from 'app/components/Spinner';
import StepCarries from './StepCarries';
import StepPeriods from './StepPeriods';
import StepOptions from './StepOptions';
import StepReview from './StepReview';
import './RollForwardWizard.scss';
import { Typography } from '../../../components/ui/Typography/Typography';

const TOTAL_STEPS = 4;
const DATE_FMT = 'YYYY-MM-DD';

const toDate = (value) => (value ? moment(value).format(DATE_FMT) : null);
const shiftDate = (value) => (value ? moment(value, DATE_FMT).add(1, 'year').format(DATE_FMT) : value);

const RollForwardWizard = ({ show, documentVersion, selectedOrganization, onClose }) => {
  const documentSuperId = documentVersion?.documentSuperId;
  const documentRevisionId = documentVersion?.documentRevisionId;

  const { data: preview, isLoading } = useClonePreview(documentSuperId, documentRevisionId);
  const { mutate: cloneDocument, isLoading: isCloning, isSuccess, isError } = useCloneDocument();

  const [step, setStep] = useState(1);
  const [periods, setPeriods] = useState({});
  const [keepDismissals, setKeepDismissals] = useState(false);

  const editableContexts = useMemo(() => (preview?.contexts ?? []).filter((c) => !c.periodForever), [preview]);

  useEffect(() => {
    const init = {};
    (preview?.contexts ?? []).forEach((c) => {
      init[c.contextId] = { periodStartDate: toDate(c.periodStartDate), periodEndDate: toDate(c.periodEndDate), periodInstant: toDate(c.periodInstant) };
    });
    setPeriods(init);
    setStep(1);
    setKeepDismissals(false);
  }, [preview]);

  useEffect(() => {
    if (isSuccess || isError) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  if (!isLoading && (!preview || preview.tagCount === 0)) {
    return <CloneDocumentModal show={show} documentVersion={documentVersion} selectedOrganization={selectedOrganization} onClose={onClose} />;
  }

  const loading = isLoading || !preview;

  const setContextField = (contextId, field, value) => setPeriods((prev) => ({ ...prev, [contextId]: { ...prev[contextId], [field]: value } }));

  const shiftAll = () =>
    setPeriods((prev) => {
      const next = { ...prev };
      editableContexts.forEach((c) => {
        const cur = prev[c.contextId] ?? {};
        next[c.contextId] = {
          periodStartDate: shiftDate(cur.periodStartDate),
          periodEndDate: shiftDate(cur.periodEndDate),
          periodInstant: shiftDate(cur.periodInstant),
        };
      });
      return next;
    });

  const buildPeriodOverrides = () => {
    const overrides = {};
    editableContexts.forEach((c) => {
      const cur = periods[c.contextId] ?? {};
      const changed =
        cur.periodStartDate !== toDate(c.periodStartDate) || cur.periodEndDate !== toDate(c.periodEndDate) || cur.periodInstant !== toDate(c.periodInstant);
      if (changed) {
        overrides[c.contextId] = {
          periodStartDate: cur.periodStartDate ?? null,
          periodEndDate: cur.periodEndDate ?? null,
          periodInstant: cur.periodInstant ?? null,
        };
      }
    });
    return overrides;
  };

  const onClone = () => {
    cloneDocument({
      documentSuperId,
      documentRevisionId,
      selectedOrganization,
      rollForward: { periodOverrides: buildPeriodOverrides(), keepDismissals },
    });
  };

  const body = () => {
    if (step === 1) return <StepCarries preview={preview} />;
    if (step === 2) return <StepPeriods contexts={editableContexts} periods={periods} onChange={setContextField} onShiftAll={shiftAll} />;
    if (step === 3) return <StepOptions keepDismissals={keepDismissals} onChange={setKeepDismissals} />;
    return <StepReview preview={preview} periodOverrides={buildPeriodOverrides()} keepDismissals={keepDismissals} />;
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} dialogClassName="roll-forward-dialog" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {i18n('roll-forward.title')} — {i18n('roll-forward.step-counter')} {step}/{TOTAL_STEPS}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="roll-forward__loading">
            <Spinner />
          </div>
        ) : (
          body()
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {i18n('roll-forward.cancel')}
        </Button>
        {!loading && step > 1 ? (
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
            {i18n('roll-forward.back')}
          </Button>
        ) : null}
        {loading || step < TOTAL_STEPS ? (
          <Button variant="primary" disabled={loading} onClick={() => setStep((s) => s + 1)}>
            {i18n('roll-forward.next')}
          </Button>
        ) : (
          <Button variant="primary" disabled={isCloning} onClick={onClone}>
            {isCloning ? <i className="fa-regular fa-circle-notch fa-spin mr-2" /> : null}
            {i18n('roll-forward.clone')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RollForwardWizard;
